import type { CustomerOverview, TileFacts, TileKey, TileRow, Unternehmen } from "@kundeninfo/api-client";
import type { ListenkachelKennzahl, ListenkachelStatusArt, Objektart } from "@kundeninfo/ui/me-design";
import type { AktenTabId } from "../KundenAkte.js";
import { ACTIVITY_TILE_MAPPINGS } from "./activityTiles.js";
import { DOCUMENT_TILE_MAPPINGS } from "./documentTiles.js";

/**
 * Was die Übersicht von einer Kachel weiß: ihr Symbol, ihr Reiter („weitere“ und der Kopf führen dorthin) und ob sie
 * zu den Belegkacheln gehört (nur diese folgen der Unternehmenswahl). Titel, Leertext und „weitere“-Text stehen in
 * `TILE_TEXT` (texts.ts).
 */
export type TileMeta = { art: Objektart; tab: AktenTabId; documentBound: boolean };

export const TILE_CATALOG: Record<TileKey, TileMeta> = {
  importantNotes: { art: "hinweis", tab: "wichtiges", documentBound: false },
  tickets: { art: "ticket", tab: "tickets", documentBound: false },
  invoices: { art: "rechnung", tab: "rechnungen", documentBound: true },
  billing: { art: "abrechnung", tab: "auftraege", documentBound: true },
  // Lieferanten Strang E (Aufgabe 21): hinter „Abrechnung“, die Reiter kommen mit Aufgabe 22.
  supplierContracts: { art: "vertrag", tab: "liefervertraege", documentBound: true },
  incomingInvoices: { art: "eingangsrechnung", tab: "eingangsrechnungen", documentBound: true },
  offers: { art: "angebot", tab: "angebote", documentBound: true },
  orders: { art: "auftrag", tab: "auftraege", documentBound: true },
  notes: { art: "notiz", tab: "notizen", documentBound: false },
  files: { art: "datei", tab: "dateien", documentBound: false },
  group: { art: "konzern", tab: "konzern", documentBound: false },
  monitor: { art: "mandant", tab: "stammdaten", documentBound: false },
};

/** Die Kacheln, die der Server liefert; der Monitor lädt für sich. */
export type ServerTileKey = keyof CustomerOverview["tiles"];

/** `today` ist der Tag vom Server; die Kürzel erscheinen nur, wenn der Kunde mehrere Unternehmensbeziehungen hat. */
export type TileContext = { today: string; companies: readonly Unternehmen[]; showCompanyCodes: boolean };

/** Eine Zeile der Listenkachel als Anzeigedaten (`data-*` an der `<option>`). */
export type TileRowView = {
  kennung?: string; text: string; zusatz?: string; unter?: string; statusArt?: ListenkachelStatusArt; status?: string;
};

/** Wie eine Kachel ihre Kennzahlen und Zeilen zeigt; der Rahmen (`TileFrame` in TileView.tsx) ist für alle gleich. */
export type TileMapping = {
  facts: (f: TileFacts, ctx: TileContext) => ListenkachelKennzahl[];
  row: (r: TileRow, ctx: TileContext) => TileRowView;
};

/** Die Abbildung jeder Serverkachel; der Typprüfer meldet jede, die fehlt. */
export const TILE_MAPPINGS: Record<ServerTileKey, TileMapping> = { ...DOCUMENT_TILE_MAPPINGS, ...ACTIVITY_TILE_MAPPINGS };

export const TILE_ID_PREFIX = "cov-tile-";
/** Die Kennung der Kachel im Dokument. */
export const tileId = (key: TileKey): string => `${TILE_ID_PREFIX}${key}`;
